import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { GOALS, goalFor, PIECE_TYPE_ICON, PIECE_TYPE_LABEL } from "@/lib/campaigns";
import { Megaphone, Plus, Lock, Sparkles, ArrowRight, Loader2, CalendarDays, BookOpen } from "lucide-react";

export default function Campaigns() {
  const nav = useNavigate();
  const [state, setState] = useState({ campaigns: [], can_use: true, block_reason: "", flat_cost: 20, is_unlimited: false, loading: true });

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/campaigns"); setState({ ...data, loading: false }); }
    catch { setState((s) => ({ ...s, loading: false })); }
  })(); }, []);

  if (state.loading) {
    return <div data-testid="campaigns-loading" className="flex items-center justify-center min-h-[60vh] text-[#64748B]">Surfacing campaigns…</div>;
  }

  if (!state.can_use) {
    return (
      <div className="max-w-4xl mx-auto px-5 lg:px-10 py-8 lg:py-12" data-testid="campaigns-locked">
        <div className="relative min-h-[60vh] flex items-center justify-center rounded-3xl overflow-hidden mt-4">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/3721304/pexels-photo-3721304.jpeg?auto=compress&cs=tinysrgb&h=650&w=940')" }} />
          <div className="absolute inset-0 bg-[#0A2342]/85 backdrop-blur-xl" />
          <div className="relative z-10 bg-white/10 border border-white/20 backdrop-blur-2xl p-12 max-w-lg text-center rounded-3xl shadow-2xl">
            <div className="w-14 h-14 rounded-full bg-[#4FD1C5]/20 border border-[#4FD1C5]/40 flex items-center justify-center mx-auto mb-5">
              <Lock className="w-6 h-6 text-[#4FD1C5]" />
            </div>
            <div className="font-heading font-bold text-3xl text-white leading-tight">Campaign Builder</div>
            <div className="text-[13px] tracking-[0.22em] uppercase text-[#4FD1C5] mt-3">Pro & Pro Unlimited</div>
            <p className="text-white/85 text-[14.5px] mt-5 leading-relaxed">
              {state.block_reason || "Available on Pro + Pro Unlimited plans."}
            </p>
            <button onClick={() => nav("/billing")} data-testid="campaign-locked-upgrade-btn" className="mt-7 inline-flex items-center gap-2 bg-[#4FD1C5] text-[#0A2342] font-heading font-bold text-[14px] px-6 py-3 rounded-xl hover:bg-white transition-all hover:scale-[1.02] active:scale-[0.99] shadow-lg">
              See paid plans <ArrowRight className="w-4 h-4" />
            </button>
            <div className="mt-4 text-[12.5px]">
              <Link
                to="/downloads/how-to-execute-campaigns"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="campaigns-locked-guide-link"
                className="text-white/70 hover:text-white underline underline-offset-4 inline-flex items-center gap-1.5"
              >
                <BookOpen className="w-3.5 h-3.5" />
                Preview the launch playbook
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-10 py-8 lg:py-12" data-testid="campaigns-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-md bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center"><Megaphone className="w-5 h-5" /></div>
          <div>
            <h1 className="font-heading font-bold text-3xl lg:text-4xl text-[#0A2342] leading-tight tracking-tight">Campaigns</h1>
            <p className="text-[15px] text-[#475569] mt-1 max-w-2xl">Time-boxed content pushes — reels, carousels, hooks, blogs and emails wired to one narrative arc.</p>
          </div>
        </div>
        <button onClick={() => nav("/campaigns/new")} data-testid="campaign-create-btn" className="dms-btn-primary inline-flex items-center gap-2 px-5 py-2.5 self-start">
          <Plus className="w-4 h-4" /> New campaign
        </button>
      </div>

      <div className="mb-8 flex items-center gap-3 rounded-md border border-[#E2E8F0] bg-white px-4 py-3" data-testid="campaigns-how-to-publish">
        <div className="w-8 h-8 rounded-md bg-[#0A2342]/6 text-[#0A2342] flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-4 h-4" />
        </div>
        <div className="flex-1 text-[13px] text-[#475569] leading-snug">
          Ready to execute a campaign? Follow the multi-channel launch playbook.
        </div>
        <Link
          to="/downloads/how-to-execute-campaigns"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342] whitespace-nowrap"
        >
          How to publish <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {state.campaigns.length === 0 ? (
        <div className="dms-card p-12 text-center border-dashed border-2 border-[#E2E8F0]">
          <Sparkles className="w-9 h-9 text-[#4FD1C5] mx-auto mb-4" />
          <div className="font-heading font-bold text-xl text-[#0A2342]">No campaigns yet</div>
          <p className="text-[13.5px] text-[#64748B] mt-2 max-w-md mx-auto">
            {state.is_unlimited ? "Pro Unlimited — campaigns are free. Fire up your first." : `Pro plan — each campaign costs a flat ${state.flat_cost} credits.`}
          </p>
          <button onClick={() => nav("/campaigns/new")} className="dms-btn-primary mt-5 inline-flex items-center gap-2 px-5 py-2.5">
            Start your first campaign <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {state.campaigns.map((c) => <CampaignCard key={c.id} c={c} onOpen={() => nav(`/campaigns/${c.id}`)} />)}
        </div>
      )}
    </div>
  );
}

function CampaignCard({ c, onOpen }) {
  const g = goalFor(c.goal_key);
  const Icon = g.icon;
  const isGenerating = c.status === "generating";
  const isReady = c.status === "ready";
  const isFailed = c.status === "failed";
  const pieceCount = (c.plan || []).length || (
    (c.mix?.reels || 0) + (c.mix?.carousels || 0) + (c.mix?.hooks ? 1 : 0) + (c.mix?.blog ? 1 : 0) + (c.mix?.email ? 1 : 0)
  );

  return (
    <button
      onClick={onOpen}
      data-testid={`campaign-list-card-${c.id}`}
      className="dms-card p-6 text-left hover:-translate-y-0.5 hover:shadow-lg hover:border-[#4FD1C5]/60 transition-all group relative overflow-hidden"
    >
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10" style={{ backgroundColor: g.color }} />
      <div className="flex items-start justify-between gap-3 mb-4 relative">
        <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${g.color}22`, color: g.color }}>
          <Icon className="w-5 h-5" />
        </div>
        {isGenerating && <span className="dms-badge bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]/30 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Generating</span>}
        {isReady && <span className="dms-badge bg-[#ECFDF5] text-[#065F46] border-[#10B981]/30">Ready</span>}
        {isFailed && <span className="dms-badge bg-[#FEF2F2] text-[#B91C1C] border-[#EF4444]/30">Failed</span>}
        {c.status === "draft" && <span className="dms-badge bg-[#F1F5F9] text-[#64748B] border-[#E2E8F0]">Draft</span>}
      </div>
      <div className="font-heading font-bold text-[17px] text-[#0A2342] leading-snug">{c.name}</div>
      <div className="text-[12px] text-[#64748B] mt-1">{g.name}</div>
      <div className="flex items-center gap-3 mt-4 text-[12px] text-[#475569]">
        <span className="inline-flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {c.duration_days} days</span>
        <span className="text-[#CBD5E1]">·</span>
        <span>{pieceCount} pieces</span>
        {c.credits_spent > 0 && <><span className="text-[#CBD5E1]">·</span><span>{c.credits_spent} credits</span></>}
      </div>
    </button>
  );
}
